import { utf8Encode } from './codec';

/**
 * Stable 64-bit hashing.
 *
 * Implemented with BigInt and explicit masking so the result is exactly defined by this
 * source file, independent of engine number semantics. It is used for seed derivation,
 * board hashes, cache validation and duplicate detection — never for security.
 */

export const MASK64 = (1n << 64n) - 1n;

const FNV_OFFSET_BASIS = 0xcbf29ce484222325n;
const FNV_PRIME = 0x00000100000001b3n;

export function fnv1a64(bytes: Uint8Array): bigint {
  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < bytes.length; i += 1) {
    hash ^= BigInt(bytes[i]);
    hash = (hash * FNV_PRIME) & MASK64;
  }
  return hash;
}

/** SplitMix64 finalizer — improves avalanche of the FNV output. */
export function mix64(value: bigint): bigint {
  let z = value & MASK64;
  z = ((z ^ (z >> 30n)) * 0xbf58476d1ce4e5b9n) & MASK64;
  z = ((z ^ (z >> 27n)) * 0x94d049bb133111ebn) & MASK64;
  return (z ^ (z >> 31n)) & MASK64;
}

export type HashPart = string | number | bigint | boolean;

/**
 * Canonically encodes each part with an explicit type tag and a record separator so that
 * `("a", "b")` and `("ab")` can never collide, and numeric values never depend on locale
 * or float formatting. Only safe integers are accepted for `number`.
 */
const RECORD_SEPARATOR = '\u001f';

export function encodeHashParts(parts: readonly HashPart[]): string {
  let encoded = '';
  for (const part of parts) {
    if (typeof part === 'string') {
      encoded += `s:${part}${RECORD_SEPARATOR}`;
    } else if (typeof part === 'bigint') {
      encoded += `b:${(part & MASK64).toString(16)}${RECORD_SEPARATOR}`;
    } else if (typeof part === 'boolean') {
      encoded += `t:${part ? 1 : 0}${RECORD_SEPARATOR}`;
    } else {
      if (!Number.isSafeInteger(part)) {
        throw new TypeError(`Hash parts must be safe integers, received ${String(part)}`);
      }
      encoded += `n:${part.toString(10)}${RECORD_SEPARATOR}`;
    }
  }
  return encoded;
}

export function stableHash64(...parts: readonly HashPart[]): bigint {
  return mix64(fnv1a64(utf8Encode(encodeHashParts(parts))));
}

export function hashBytes64(bytes: Uint8Array): bigint {
  return mix64(fnv1a64(bytes));
}

/** Fixed-width lowercase hex, always 16 characters. */
export function toHex64(value: bigint): string {
  return (value & MASK64).toString(16).padStart(16, '0');
}

export function fromHex64(hex: string): bigint {
  if (!/^[0-9a-f]{1,16}$/.test(hex)) {
    throw new Error(`Not a 64-bit hex string: ${hex}`);
  }
  return BigInt(`0x${hex}`) & MASK64;
}
